import ioClient from "chzzk-socket"; // socket.io-client v2 (치지직 공식 Session은 v2 프로토콜)
import Source from "../../Source";
import SignalingApi from "@/api/SignalingApi";

// 치지직 채팅/후원/구독을 브라우저에서 직결 수신 (event-stream / direct).
//  - signaling Edge로 세션 발급(broadcast_token)·구독만 위임(OAuth 토큰은 서버에만) → 스트림은 브라우저↔치지직 직결.
//  - 흐름: chzzkSession(token)→url → socket(v2) 연결 → SYSTEM(connected).sessionKey
//          → chzzkSubscribe(token,sessionKey) → CHAT/DONATION/SUBSCRIPTION 수신 → normalize → _emit.
//  - 세션은 단기·재발급(갱신X) → 예기치 못한 끊김 시 url 재발급 + 재연결 + 재구독(백오프).
//  - 미연동(409)이면 status="unconfigured" 후 정지(끊김 빨강 아님 — 애초에 안 쓰는 펫).
const KINDS = ["chat", "donation", "subscription"];

export default class ChzzkDirectSource extends Source {
  constructor({ token } = {}) {
    super({ provider: "chzzk", kind: "event-stream", mode: "direct" });
    this._token = token;
    this._socket = null;
    this._closedByUs = false;
    this._reconnectTimer = null;
  }

  open() {
    if (!this._token) {
      console.warn("E_NO_TOKEN : can not open chzzk direct stream");
      return this;
    }
    this._closedByUs = false;
    this._connect();
    return this;
  }

  async _connect() {
    this._setStatus("connecting");
    let url;
    try {
      url = await SignalingApi.chzzkSession(this._token); // 세션 발급(RPC)
    } catch (e) {
      // 409 = 이 펫은 치지직 미연동 → 재시도/에러표시 없이 조용히 정지
      if (e.status === 409) {
        this._setStatus("unconfigured");
        return;
      }
      this._setStatus("failed");
      this._scheduleReconnect();
      return;
    }
    if (this._closedByUs) return;

    const socket = ioClient(url, { transports: ["websocket"], reconnection: false, forceNew: true });
    this._socket = socket;

    socket.on("SYSTEM", (raw) => this._onSystem(raw, socket));
    socket.on("CHAT", (raw) => this._emit(normalizeChat(raw)));
    socket.on("DONATION", (raw) => this._emit(normalizeDonation(raw)));
    socket.on("SUBSCRIPTION", (raw) => this._emit(normalizeSubscription(raw)));

    socket.on("disconnect", () => {
      if (this._closedByUs) return;
      this._setStatus("connecting");
      this._scheduleReconnect(); // 세션 만료/끊김 → url 재발급부터 다시
    });
    socket.on("connect_error", () => {
      try {
        socket.close();
      } catch (error) {
        console.error(error);
      }
    });
  }

  async _onSystem(raw, socket) {
    const obj = typeof raw === "string" ? safeParse(raw) : raw;
    if (obj?.type !== "connected") return;
    const sessionKey = obj?.data?.sessionKey ?? obj?.sessionKey;
    if (!sessionKey || socket !== this._socket) return;
    try {
      // 연결마다 고유 sessionKey → 서버가 OAuth로 대신 구독(RPC)
      await SignalingApi.chzzkSubscribe(this._token, sessionKey, KINDS);
      this._setStatus("ready");
    } catch (error) {
      console.error("[chzzk-direct] subscribe failed", error);
      this._setStatus("failed");
    }
  }

  _scheduleReconnect() {
    if (this._closedByUs || this._reconnectTimer) return;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._teardownSocket();
      this._connect();
    }, 3000);
  }

  _teardownSocket() {
    if (!this._socket) return;
    try {
      this._socket.disconnect();
    } catch (error) {
      console.error(error);
    }
    this._socket = null;
  }

  close() {
    this._closedByUs = true;
    this._setStatus("closed");
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._teardownSocket();
  }
}

// ===== 공식 payload → 도메인 StreamEvent 매핑 (서버 OfficialStreamSource에서 이관, direct는 클라가 파싱) =====
function normalizeChat(raw) {
  const o = (typeof raw === "string" ? safeParse(raw) : raw) || {};
  const profile = parseProfile(o.profile);
  return {
    type: "CHAT",
    message: o.content ?? o.message ?? "",
    hidden: false,
    profile: { nickname: profile?.nickname ?? o.nickname ?? "익명" },
  };
}

function normalizeDonation(raw) {
  const o = (typeof raw === "string" ? safeParse(raw) : raw) || {};
  const profile = parseProfile(o.donatorProfile ?? o.profile);
  const nickname = profile?.nickname ?? o.donatorNickname ?? null;
  return {
    type: "DONATION",
    profile: nickname ? { nickname } : null, // 익명 후원이면 null
    extras: { payAmount: Number(o.payAmount ?? o.extras?.payAmount ?? 0) },
  };
}

function normalizeSubscription(raw) {
  const o = (typeof raw === "string" ? safeParse(raw) : raw) || {};
  const profile = parseProfile(o.subscriberProfile ?? o.profile);
  return {
    type: "SUBSCRIPTION",
    profile: { nickname: profile?.nickname ?? o.subscriberNickname ?? "익명" },
    extras: { month: Number(o.month ?? 1), tierName: o.tierName ?? "", tierNo: Number(o.tierNo ?? 0) },
  };
}

// 치지직은 profile을 JSON 문자열로 주는 경우가 있어 방어적으로 파싱.
function parseProfile(p) {
  if (!p) return null;
  if (typeof p === "string") return safeParse(p);
  return p;
}

function safeParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}
